import { useFrame } from "@react-three/fiber";
import {
  CapsuleCollider,
  RapierRigidBody,
  RigidBody,
  vec3,
} from "@react-three/rapier";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Group, Vector3 } from "three";
import Entity, { EntityStates } from "./Actors/Entity";
import EntityCorruptionBar from "./HUD/EntityCorruptionBar";
import { useGameStore } from "../stores/gameStore";
import { useEntityAnimationStateManager } from "../stores/useEntityAnimationStateManager";
import { ScrollStates } from "./Assets/Scroll";
import { GameStates } from "../constants/GameStates";
import { PouchStates } from "./Assets/Pouch";

/**
 * Generating random position for the entity
 */
const newPosition = (box: number, boundary: number) => {
  return (
    boundary / 2 -
    box / 2 -
    (boundary - box) * (Math.round(Math.random() * 100) / 100)
  );
};

const EntityAI = ({
  // Spell casting
  onCastSpell,
}: EntityAIProps) => {
  const SIGHT_RANGE = 15;
  const ENTITY_SPEED = 0.01;
  const AVOID_DISTANCE = 1;
  const ARRIVAL_THRESHOLD = 3;
  const STOP_THRESHOLD = 3;
  const NUM_SAMPLES_FOR_SMOOTHING = 10;
  const CAST_RATE = 1250;
  const MIN_PATROL_POINT_DISTANCE = 10;
  const MAX_PATROL_POINT_DISTANCE = 20;

  const angleToPlayer = useMemo(() => new THREE.Euler(), []);

  // Animation change functions
  const idleAnimation = useEntityAnimationStateManager((state) => state.idle);
  const walkAnimation = useEntityAnimationStateManager((state) => state.walk);
  const runAnimation = useEntityAnimationStateManager((state) => state.run);
  const castSpellAnimation = useEntityAnimationStateManager(
    (state) => state.castSpell
  );
  const recieveHitAnimation = useEntityAnimationStateManager(
    (state) => state.recieveHit
  );
  const releaseAnimation = useEntityAnimationStateManager(
    (state) => state.release
  );
  const { gameState } = useGameStore((state: any) => ({
    gameState: state.gameState,
  }));

  const entityRef = useRef<RapierRigidBody>(null);
  const entityModelRef = useRef<Group>(null);

  const { storedTreeData, playerPosition, entityPosition, playerStrength } =
    useGameStore((state: any) => ({
      storedTreeData: state.storedTreeData,
      playerPosition: state.playerPosition,
      entityPosition: state.entityPosition,
      playerStrength: state.playerStrength,
    }));
  const getQuestion = useGameStore((state: any) => state.getQuestion);

  const { entity, setEntity } = useGameStore((state: any) => ({
    entity: state.entity,
    setEntity: state.setEntity,
  }));

  const { pouchPosition, setPouchState } = useGameStore((state: any) => ({
    pouchPosition: state.pouchPosition,
    setPouchState: state.setPouchState,
  }));

  const { pouchValue, setPouchValue } = useGameStore((state: any) => ({
    pouchValue: state.pouchValue,
    setPouchValue: state.setPouchValue,
  }));

  const { scrollPosition, setScrollState } = useGameStore((state: any) => ({
    scrollPosition: state.scrollPosition,
    setScrollState: state.setScrollState,
  }));

  const [curEntityPosition] = useState(entity.originPosition);

  const [targetLastKnownPosition, setTargetLastKnownPosition] = useState(
    new Vector3()
  );
  const [leftPosition, setLeftPosition] = useState(false);
  const [hasCheckedLastKnownPosition, setHasCheckedLastKnownPosition] =
    useState(true);
  const [isWaiting, setIswaiting] = useState(false);
  const isHitBySpell = useRef(false);
  const isCastingSpell = useRef(false);

  // can cast spell setup
  const lastCastSpell = useRef(0);

  useEffect(() => {
    /**
     * Timing based spell casting
     */
    const intervalId = setInterval(() => {
      if (
        entity.canSeeTarget &&
        gameState === GameStates.GAME &&
        entity.state === EntityStates.CHASE
      ) {
        // Calculate angle to player
        angleToPlayer.y =
          entityModelRef.current!.rotation.x !== 0
            ? entityModelRef.current!.rotation.y < Math.PI &&
              entityModelRef.current!.rotation.y > Math.PI / 2
              ? Math.PI + entityModelRef.current!.rotation.y // 2nd quadrant
              : Math.PI - entityModelRef.current!.rotation.y // 3rd quadrant
            : entityModelRef.current!.rotation.y; // 1st and 4th quadrants
        // Casting a spell
        isCastingSpell.current! = true;
        setEntity({ state: EntityStates.CAST_SPELL });
        lastCastSpell.current = Date.now();
        const newSpell = {
          id: "entity-" + +new Date(),
          position: vec3(entityRef.current!.translation()),
          angle: angleToPlayer.y,
          caster: "Entity",
        };
        castSpellAnimation();
        onCastSpell(newSpell);
        isCastingSpell.current! = false;
      }
    }, CAST_RATE);
    return () => clearInterval(intervalId);
  }, [angleToPlayer, entity, castSpellAnimation, gameState, onCastSpell]);

  const velocitySamples: Vector3[] = []; // Array to store velocity samples for smoothing
  /**
   * Makes the entity look in the direction it is moving.
   *
   * @param position - The current position of the entity.
   * @param velocity - The current velocity of the entity.
   * @param smoothing - Whether to smooth the rotation.
   */
  const lookWhereGoing = (
    position: Vector3,
    velocity: Vector3,
    smoothing: boolean
  ) => {
    if (!entityModelRef.current) return;
    let direction = position.clone().add(velocity).setY(position.y);

    if (smoothing) {
      let smoothedVelocity = new Vector3(0, 0, 0);
      velocitySamples.forEach((sample) => smoothedVelocity.add(sample));
      smoothedVelocity.divideScalar(velocitySamples.length);
      direction = position.clone().add(smoothedVelocity).setY(position.y);
    }

    entityModelRef.current.lookAt(direction);
  };

  /**
   * Moves the entity towards the specified target position while avoiding obstacles.
   *
   * @param target - The target position as a THREE.Vector3.
   * @param obstacles - Array of arrays where the first element is a Vector3 representing obstacle positions.
   * @param smoothing - Whether to smooth the rotation.
   */
  const seekWithAvoidance = (
    target: Vector3,
    obstacles: [Vector3][],
    smoothing: boolean = true
  ) => {
    if (!entityRef.current) return;

    // Convert Rapier vector to THREE.Vector3
    const entityPosition = new Vector3(
      entityRef.current.translation().x,
      entityRef.current.translation().y,
      entityRef.current.translation().z
    );

    // Calculate desired velocity towards the target with arrival behavior
    let desiredVelocity = target.clone().sub(entityPosition);
    const distance = entityPosition.distanceTo(target);

    // If within stop threshold, stop the entity
    if (distance < STOP_THRESHOLD && entity.canSeeTarget) {
      desiredVelocity.set(0, 0, 0);
      entityRef.current.setNextKinematicTranslation(entityPosition); // Stop the entity
      return;
    }

    if (distance > ARRIVAL_THRESHOLD) {
      desiredVelocity.normalize().multiplyScalar(ENTITY_SPEED);
    } else if (entity.canSeeTarget) {
      desiredVelocity
        .normalize()
        .multiplyScalar(ENTITY_SPEED * (distance / ARRIVAL_THRESHOLD));
    }

    // Compute the 'ahead' and 'ahead2' vectors for obstacle detection
    const dynamicLength = desiredVelocity.length() / ENTITY_SPEED;
    const ahead = entityPosition
      .clone()
      .add(desiredVelocity.clone().normalize().multiplyScalar(dynamicLength));
    const ahead2 = entityPosition.clone().add(
      desiredVelocity
        .clone()
        .normalize()
        .multiplyScalar(AVOID_DISTANCE * 0.5)
    );

    let mostThreatening: Vector3 | null = null;
    let minDistance = Infinity;

    // Find the most threatening obstacle
    obstacles.forEach(([obstaclePosition]) => {
      const collision =
        obstaclePosition.distanceTo(ahead) <= AVOID_DISTANCE ||
        obstaclePosition.distanceTo(ahead2) <= AVOID_DISTANCE;

      if (collision) {
        const distanceToObstacle = entityPosition.distanceTo(obstaclePosition);
        if (distanceToObstacle < minDistance) {
          minDistance = distanceToObstacle;
          mostThreatening = obstaclePosition;
        }
      }
    });

    // Calculate avoidance force
    const avoidance = new Vector3(0, 0, 0);

    if (mostThreatening) {
      avoidance
        .copy(ahead.clone().sub(mostThreatening))
        .normalize()
        .multiplyScalar(ENTITY_SPEED);
    }

    // Combine the desired velocity with the avoidance force
    const combinedVelocity = desiredVelocity.add(avoidance);

    // Ensure the combined velocity doesn't exceed the entity's max speed
    if (combinedVelocity.length() > ENTITY_SPEED) {
      combinedVelocity.normalize().multiplyScalar(ENTITY_SPEED);
    }

    // Set the new position based on the combined velocity
    const newPosition = entityPosition.clone().add(combinedVelocity);
    entityRef.current.setNextKinematicTranslation(newPosition);

    // Update velocity samples for smoothing
    if (smoothing) {
      if (velocitySamples.length === NUM_SAMPLES_FOR_SMOOTHING) {
        velocitySamples.shift();
      }
      velocitySamples.push(combinedVelocity.clone().setY(entityPosition.y));
    }

    // Make the entity look in the direction it is moving
    lookWhereGoing(entityPosition, combinedVelocity, smoothing);
  };

  /**
   * Generates a random endpoint within a spherical shell around a given position.
   *
   * @param originalPosition - The central position from which the random point will be calculated.
   * @param minDistance - The minimum distance from the original position the new point can be.
   * @param maxDistance - The maximum distance from the original position the new point can be.
   * @returns A `THREE.Vector3` object representing the randomly generated endpoint.
   */
  const getRandomPointWithinRadius = (
    originalPosition: THREE.Vector3,
    minDistance: number,
    maxDistance: number
  ) => {
    const randomDirection = new THREE.Vector3(
      Math.random() * 2 - 1,
      0,
      Math.random() * 2 - 1
    ).normalize();

    const randomDistance =
      Math.random() * (maxDistance - minDistance) + minDistance;
    const randomOffset = randomDirection.multiplyScalar(randomDistance);

    return originalPosition.clone().add(randomOffset);
  };

  const [patrolPathEndPosition, setPatrolPathEndPosition] = useState(
    getRandomPointWithinRadius(
      entity.originPosition,
      MIN_PATROL_POINT_DISTANCE,
      MAX_PATROL_POINT_DISTANCE
    )
  );
  const path = [entity.originPosition, patrolPathEndPosition];
  // Initialize the path index for the entity
  const [pathIndex, setPathIndex] = useState(0);

  /**
   * Follows a path of waypoints while avoiding obstacles.
   * Also makes the character look in the direction it is moving.
   *
   * @param path - An array of THREE.Vector3 representing the path waypoints.
   * @param loop - Whether the path should loop.
   * @param thresholdRadius - The radius within which the entity switches to the next waypoint.
   * @param obstacles - Array of arrays where the first element is a Vector3 representing obstacle positions.
   * @param smoothing - Whether to smooth the rotation.
   */
  const followPathWithAvoidance = (
    path: Vector3[],
    loop: boolean,
    thresholdRadius: number = 2,
    obstacles: [Vector3][],
    smoothing: boolean = true
  ) => {
    if (!entityRef.current) return;

    // Convert Rapier vector to THREE.Vector3
    const entityPosition = new Vector3(
      entityRef.current.translation().x,
      entityRef.current.translation().y,
      entityRef.current.translation().z
    );

    const wayPoint = path[pathIndex];

    if (!wayPoint) return;

    const distanceToWayPoint = entityPosition.distanceTo(wayPoint);
    if (distanceToWayPoint < thresholdRadius) {
      if (pathIndex >= path.length - 1) {
        if (loop) {
          setPathIndex(0);
        }
      } else {
        setPathIndex(pathIndex + 1);
      }
    }

    // Calculate the next target waypoint after path index adjustment
    const targetWayPoint = path[pathIndex];

    // Calculate the seeking behavior
    const desiredVelocity = targetWayPoint
      .clone()
      .sub(entityPosition)
      .normalize()
      .multiplyScalar(ENTITY_SPEED * 0.5);

    // Incorporate avoidance behavior
    const entityVelocity = new Vector3(
      entityRef.current.linvel().x,
      entityRef.current.linvel().y,
      entityRef.current.linvel().z
    );
    const dynamicLength = entityVelocity.length() / (ENTITY_SPEED * 0.5);
    const ahead = entityPosition
      .clone()
      .add(entityVelocity.clone().normalize().multiplyScalar(dynamicLength));
    const ahead2 = entityPosition.clone().add(
      entityVelocity
        .clone()
        .normalize()
        .multiplyScalar(AVOID_DISTANCE * 0.5)
    );

    let mostThreatening: Vector3 | null = null;
    let minDistance = Infinity;

    obstacles.forEach(([obstaclePosition]) => {
      const collision =
        obstaclePosition.distanceTo(ahead) <= AVOID_DISTANCE ||
        obstaclePosition.distanceTo(ahead2) <= AVOID_DISTANCE;

      if (collision) {
        const distanceToObstacle = entityPosition.distanceTo(obstaclePosition);
        if (distanceToObstacle < minDistance) {
          minDistance = distanceToObstacle;
          mostThreatening = obstaclePosition;
        }
      }
    });

    // Calculate avoidance force
    const avoidance = new Vector3(0, 0, 0);

    if (mostThreatening) {
      avoidance
        .copy(ahead.clone().sub(mostThreatening))
        .normalize()
        .multiplyScalar(ENTITY_SPEED);
    }

    // Combine seeking and avoidance behaviors
    const combinedVelocity = desiredVelocity.add(avoidance);
    const newPosition = entityPosition.clone().add(combinedVelocity);

    // Set the new position
    if (pathIndex >= path.length - 1 && !loop) {
      // Arriving behavior: Stop at the waypoint
      entityRef.current.setNextKinematicTranslation(targetWayPoint.clone());
    } else {
      // Move with avoidance and seeking combined
      entityRef.current.setNextKinematicTranslation(newPosition);
    }

    // Update velocity samples for smoothing
    if (smoothing) {
      if (velocitySamples.length === NUM_SAMPLES_FOR_SMOOTHING) {
        velocitySamples.shift();
      }
      velocitySamples.push(combinedVelocity.clone().setY(entityPosition.y));
    }

    // Make the entity look in the direction it is moving
    lookWhereGoing(entityPosition, combinedVelocity, smoothing);
  };

  useFrame((state, delta) => {
    const currentPos = entityModelRef.current!.getWorldPosition(new Vector3());

    if (
      !entity.canSeeTarget &&
      currentPos.distanceTo(playerPosition[0]) <= SIGHT_RANGE
    ) {
      setEntity({ canSeeTarget: true });
    } else if (
      entity.canSeeTarget &&
      currentPos.distanceTo(playerPosition[0]) > SIGHT_RANGE
    ) {
      setEntity({ canSeeTarget: false });
    }

    entityPosition.splice(0, entityPosition.length, currentPos); // Storing entity position

    if (entity.corruptionLevel === 0) {
      // Random chance to drop coins - 20% chance
      if (Math.random() < 0.2) {
        const coinsDropped = Math.floor(Math.random() * (20 - 2 + 1)) + 10;
        pouchPosition.splice(0, pouchPosition.length, entityPosition[0]);
        setPouchState(PouchStates.PLACED);
        setPouchValue(coinsDropped);
      }
      scrollPosition.splice(0, scrollPosition.length, entityPosition[0]);
      setScrollState(ScrollStates.PLACED);
      setHasCheckedLastKnownPosition(true);
      newEntity();
    }
    if (entity.canSeeTarget) {
      handleChase(currentPos);
    }
    if (!entity.canSeeTarget && !hasCheckedLastKnownPosition && !isWaiting) {
      handleSearch(currentPos);
    }
    if (
      !entity.canSeeTarget &&
      leftPosition &&
      hasCheckedLastKnownPosition &&
      !isWaiting
    ) {
      handleReturn(currentPos);
    }
    if (!entity.canSeeTarget && isWaiting) {
      handleWait();
    }
    if (!entity.canSeeTarget && !isWaiting && !leftPosition) {
      handlePatrol();
    }
    // TODO:
    // - When entityCorruptionLevel === 0 -> show animation and spawn to another place with entityCorruptionLevel = 100

    // Set animation
    switch (entity.state) {
      case EntityStates.IDLE:
        idleAnimation();
        break;
      case EntityStates.WAIT:
        idleAnimation();
        break;
      case EntityStates.PATROL:
        walkAnimation();
        break;
      case EntityStates.CHASE:
        runAnimation();
        break;
      case EntityStates.SEARCH:
        runAnimation();
        break;
      case EntityStates.RETURN:
        runAnimation();
        break;
      case EntityStates.CAST_SPELL:
        castSpellAnimation();
        break;
      case EntityStates.RECIEVE_HIT:
        recieveHitAnimation();
        break;
      case EntityStates.RELEASE:
        releaseAnimation();
        break;
      default:
        idleAnimation();
        break;
    }
  });

  const handleChase = (currentPos: Vector3) => {
    // Go after the player
    if (entity.canSeeTarget === false) {
      setEntity({ canSeeTarget: true });
    }
    targetLastKnownPosition.set(
      playerPosition[0].x,
      currentPos.y, // The player jump doesn't have an effect
      playerPosition[0].z
    );

    if (hasCheckedLastKnownPosition) {
      setHasCheckedLastKnownPosition(false);
    }

    if (
      entity.state !== EntityStates.CHASE &&
      isHitBySpell!.current === false &&
      isCastingSpell!.current === false
    ) {
      setLeftPosition(true);
      setEntity({ state: EntityStates.CHASE });
    }
    seekWithAvoidance(targetLastKnownPosition, storedTreeData, true);
  };

  const handleSearch = (currentPos: Vector3) => {
    // Go to the position where the player was last seen
    if (currentPos.distanceTo(targetLastKnownPosition).toFixed(1) === "0.0") {
      setIswaiting(true);
    }
    if (entity.state !== EntityStates.SEARCH) {
      setEntity({ state: EntityStates.SEARCH });
    }
    seekWithAvoidance(targetLastKnownPosition, storedTreeData, true);
  };

  const handleReturn = (currentPos: Vector3) => {
    // Return to original position
    if (entity.state !== EntityStates.RETURN) {
      setEntity({ state: EntityStates.RETURN });
    }
    if (currentPos.distanceTo(entity.originPosition).toFixed(1) === "0.0") {
      setLeftPosition(false);
    }
    seekWithAvoidance(entity.originPosition, storedTreeData, true);
  };

  const handleWait = () => {
    // Wait for 2 seconds
    if (entity.state !== EntityStates.WAIT) {
      setEntity({ state: EntityStates.WAIT });
    }
    if (entity.canSeePlayer) {
      setEntity({ canSeeTarget: false });
    }
    setTargetLastKnownPosition(new Vector3());
    setTimeout(() => {
      setIswaiting(false);
    }, 1500);
    if (!hasCheckedLastKnownPosition) {
      setHasCheckedLastKnownPosition(true);
    }
  };

  const handlePatrol = () => {
    // Start patroling
    if (entity.state !== EntityStates.PATROL) {
      setEntity({ state: EntityStates.PATROL });
    }
    followPathWithAvoidance(path, true, 2, storedTreeData, true);
  };

  const onPlayerSpellHit = () => {
    isHitBySpell.current! = true;
    setEntity({ state: EntityStates.RECIEVE_HIT });
    if (entity.corruptionLevel === playerStrength) {
      getQuestion();
    } else if (
      entity.corruptionLevel !== playerStrength &&
      entity.corruptionLevel > 0
    ) {
      const newCorruptionLevel = entity.corruptionLevel - playerStrength;
      setEntity({ corruptionLevel: newCorruptionLevel });
    }
    isHitBySpell.current! = false;
  };

  /**
   * Adding entity at a new position
   */
  const newEntity = () => {
    const newOriginPosition = new Vector3(
      newPosition(20, 70),
      0,
      newPosition(20, 70)
    );
    setEntity({ corruptionLevel: 100 });
    setEntity({ state: EntityStates.RELEASE });
    entityRef.current!.setTranslation(newOriginPosition, true);
    setIswaiting(false);
    setLeftPosition(false);
    setEntity({
      id: "entity",
      originPosition: newOriginPosition,
      state: EntityStates.IDLE,
      corruptionLevel: 100,
      canSeeTarget: false,
    });
    setPatrolPathEndPosition(
      getRandomPointWithinRadius(
        entity.originPosition,
        MIN_PATROL_POINT_DISTANCE,
        MAX_PATROL_POINT_DISTANCE
      )
    );
  };

  return (
    <>
      <RigidBody
        name={"Entity"}
        type={"kinematicPosition"}
        colliders={false}
        scale={[0.35, 0.35, 0.35]}
        position={curEntityPosition}
        ref={entityRef}
      >
        <CapsuleCollider
          name={"entity"}
          args={[1.5, 1.5]}
          position={[0, 0.2, 0]}
          onCollisionEnter={({ manifold, target, other }) => {
            if (other.rigidBodyObject!.name === "SolSpell") {
              onPlayerSpellHit();
            }
          }}
        />
        {entity.canSeeTarget ? <EntityCorruptionBar /> : null}
        <group ref={entityModelRef}>
          <Entity />
        </group>
      </RigidBody>
    </>
  );
};

export default EntityAI;

interface EntityAIProps {
  // Spell casting
  onCastSpell: (newSpell: any) => void;
}
